import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box, Typography, Button, IconButton, Chip, Skeleton, Collapse,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { getPortfolioHealthScore } from "../../../api/portfolio_api";
import { getHealthNarrative } from "../../../api/api";
import {
  Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  ResponsiveContainer, Tooltip as RechartsTooltip,
} from "recharts";
import {
  fmtNumber, fmtPercent, gradeColor,
  RATING_CATEGORIES,
} from "./portfolioHelpers";
import ReactMarkdown from "react-markdown";
import { getCurrencySymbol, fmtL } from "../../../utils/currency";

/* ─────────────────────────────────────────────
   useTypewriter
   Reveals `text` progressively at `charsPerTick`
   characters every `interval` ms.
   Returns { displayed, isDone }
───────────────────────────────────────────── */

const ORDERED_KEYS = ["Strong Sell", "Sell", "Hold", "Buy", "Strong Buy"];
const SEGMENT_FRACTION = 1 / ORDERED_KEYS.length; // 0.2 each

const useTypewriter = (text, { charsPerTick = 6, interval = 12 } = {}) => {
  const [displayed, setDisplayed] = useState("");
  const [isDone, setIsDone] = useState(false);
  const idxRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    // Reset whenever text changes (new narrative arrived)
    setDisplayed("");
    setIsDone(false);
    idxRef.current = 0;

    if (!text) return;

    timerRef.current = setInterval(() => {
      idxRef.current += charsPerTick;
      if (idxRef.current >= text.length) {
        setDisplayed(text);
        setIsDone(true);
        clearInterval(timerRef.current);
      } else {
        setDisplayed(text.slice(0, idxRef.current));
      }
    }, interval);

    return () => clearInterval(timerRef.current);
  }, [text, charsPerTick, interval]);

  return { displayed, isDone };
};

/* ─────────────────────────────────────────────
   useBucketColors
───────────────────────────────────────────── */
const BUCKET_COLOR_FALLBACKS = {
  "Strong Sell": "#C62828",
  "Sell": "#E53935",
  "Hold": "#F59E0B",
  "Buy": "#66BB6A",
  "Strong Buy": "#00C853",
};

const useBucketColors = () => {
  const theme = useTheme();
  const b = theme.palette?.buckets;
  if (!b) return BUCKET_COLOR_FALLBACKS;
  return {
    "Strong Sell": b.strongSell,
    "Sell": b.sell,
    "Hold": b.hold,
    "Buy": b.buy,
    "Strong Buy": b.strongBuy,
  };
};


const sym = getCurrencySymbol();

/* ══════════════════════════════════════════════════════
   MARKDOWN COMPONENT MAP
══════════════════════════════════════════════════════ */
const useMarkdownComponents = (theme) => ({
  h1: ({ children }) => (
    <Typography variant="title" component="h1" gutterBottom>{children}</Typography>
  ),
  h2: ({ children }) => (
    <Typography
      variant="title"
      component="h2"
      gutterBottom
      sx={{
        fontSize: theme.typography.subtitle.fontSize,
        fontWeight: theme.typography.subtitle.fontWeight,
        color: "text.primary",
      }}
    >
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography variant="subtitle" component="h3" gutterBottom sx={{ color: "text.primary" }}>
      {children}
    </Typography>
  ),
  h4: ({ children }) => (
    <Typography variant="tableHeader" component="h4" gutterBottom sx={{ color: "text.primary" }}>
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography variant="paragraph" component="p" gutterBottom sx={{ color: "text.secondary" }}>
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ pl: 2.5, mb: 1, color: "text.secondary" }}>{children}</Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ pl: 2.5, mb: 1, color: "text.secondary" }}>{children}</Box>
  ),
  li: ({ children }) => (
    <Box component="li" sx={{ mb: 0.5 }}>
      <Typography variant="paragraph" component="span" sx={{ color: "text.secondary" }}>
        {children}
      </Typography>
    </Box>
  ),
  strong: ({ children }) => (
    <Typography
      component="strong"
      sx={{ fontWeight: "var(--font-weight-bold)", fontSize: "inherit", color: "text.primary" }}
    >
      {children}
    </Typography>
  ),
  em: ({ children }) => (
    <Typography
      component="em"
      sx={{ fontStyle: "italic", fontSize: "inherit", color: "text.secondary" }}
    >
      {children}
    </Typography>
  ),
  hr: () => (
    <Box
      component="hr"
      sx={{ border: "none", borderTop: "1px solid var(--color-border-primary)", my: 2 }}
    />
  ),
  code: ({ children }) => (
    <Box
      component="code"
      sx={{
        backgroundColor: "var(--color-bg-tertiary)",
        color: "var(--color-primary)",
        borderRadius: "var(--radius-sm)",
        px: 0.75,
        py: 0.25,
        fontFamily: "monospace",
        fontSize: "var(--font-size-sm)",
      }}
    >
      {children}
    </Box>
  ),
});

/* ══════════════════════════════════════════════════════
   AI HEALTH NARRATIVE  (with typewriter)
   FIX: ReactMarkdown only fires on the COMPLETE text.
        While typing → plain pre-wrap text (no parser).
        Once isDone  → full markdown render.
══════════════════════════════════════════════════════ */
const AIHealthNarrative = ({ aiNarrativeLoading, aiNarrative }) => {
  const theme = useTheme();
  const mdComponents = useMarkdownComponents(theme);

  const { displayed, isDone } = useTypewriter(aiNarrative, { charsPerTick: 6, interval: 12 });

  return (
    <Box
      sx={{
        backgroundColor: "var(--color-surface)",
        border: "1px solid var(--color-border-primary)",
        borderRadius: "var(--radius-lg)",
        overflow: "hidden",
        mb: "var(--spacing-lg)",
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: "var(--spacing-md)",
          py: "var(--spacing-sm)",
          borderBottom: "1px solid var(--color-border-primary)",
          display: "flex",
          alignItems: "center",
          gap: "var(--spacing-sm)",
        }}
      >
        <AutoAwesomeIcon
          sx={{
            fontSize: 18,
            color: "var(--color-primary)",
            animation:
              !isDone && !aiNarrativeLoading && aiNarrative
                ? "narrativePulse 1.2s ease-in-out infinite"
                : "none",
            "@keyframes narrativePulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.4 },
            },
          }}
        />
        <Typography
          sx={{
            color: "var(--color-text-primary)",
            fontSize: "var(--font-size-base)",
            fontWeight: "var(--font-weight-bold)",
          }}
        >
          FA Health Analysis
        </Typography>

        {/* Typing indicator dots */}
        {!aiNarrativeLoading && aiNarrative && !isDone && (
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: "3px", px: "var(--spacing-sm)" }}>
            {[0, 1, 2].map((i) => (
              <Box
                key={i}
                sx={{
                  width: 5, height: 5, borderRadius: "50%",
                  backgroundColor: "var(--color-primary)",
                  animation: "dotBounce 1s ease-in-out infinite",
                  animationDelay: `${i * 0.18}s`,
                  "@keyframes dotBounce": {
                    "0%, 100%": { transform: "translateY(0)", opacity: 0.4 },
                    "50%": { transform: "translateY(-4px)", opacity: 1 },
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* ── Body ── */}
      <Box sx={{ p: "var(--spacing-lg)" }}>
        {aiNarrativeLoading ? (
          /* Skeleton while API call is in-flight */
          <Box>
            {[100, 90, 75, 85].map((w, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                width={`${w}%`}
                height={14}
                sx={{
                  bgcolor: "var(--color-surface-hover)",
                  borderRadius: "var(--radius-sm)",
                  mb: i < 3 ? 0.75 : 0,
                }}
              />
            ))}
          </Box>

        ) : aiNarrative ? (
          isDone ? (
            /* ── DONE: full markdown render ── */
            <ReactMarkdown components={mdComponents}>{aiNarrative}</ReactMarkdown>
          ) : (
            /* ── TYPING: plain pre-wrap text, no markdown parser ──
               This prevents ReactMarkdown from choking on half-formed
               tokens like unclosed **bold** or a truncated ### heading. */
            <Box
              component="pre"
              sx={{
                m: 0,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "inherit",
                fontSize: "var(--font-size-base)",
                lineHeight: 1.7,
                color: "var(--color-text-secondary)",
                /* Blinking cursor at the end */
                "&::after": {
                  content: '"▍"',
                  display: "inline-block",
                  marginLeft: "2px",
                  color: "var(--color-primary)",
                  animation: "cursorBlink 0.7s step-end infinite",
                  "@keyframes cursorBlink": {
                    "0%, 100%": { opacity: 1 },
                    "50%": { opacity: 0 },
                  },
                },
              }}
            >
              {displayed}
            </Box>
          )
        ) : (
          <Typography
            variant="paragraph"
            sx={{
              fontSize: "var(--font-size-base)",
              color: "var(--color-text-muted)",
              fontStyle: "italic",
            }}
          >
            AI health analysis unavailable.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const scorToFraction = (score, ratingsRange) => {
  const num = Number(score);

  // ── fallback: no ratingsRange → linearly map 0-100
  if (!ratingsRange || Object.keys(ratingsRange).length === 0) {
    return Math.min(Math.max(num / 100, 0), 1);
  }

  for (let i = 0; i < ORDERED_KEYS.length; i++) {
    const key = ORDERED_KEYS[i];
    const r = ratingsRange[key];
    if (!r) continue;

    const bMin = i === 0 ? 0 : (ratingsRange[ORDERED_KEYS[i - 1]]?.max ?? r.min);
    const bMax = r.max;

    if (num <= bMax || i === ORDERED_KEYS.length - 1) {
      // clamp within bucket
      const t = bMax === bMin ? 1 : Math.min(Math.max((num - bMin) / (bMax - bMin), 0), 1);
      return (i + t) * SEGMENT_FRACTION;
    }
  }
  return 1;
};

/* ─────────────────────────────────────────────
   Semi-circle gauge
───────────────────────────────────────────── */
const SemiGauge = ({ score, ratingsRange }) => {
  const bucketColors = useBucketColors();

  const W = 280, H = 190, CX = W / 2, CY = 150;
  const RADIUS = 110, STROKE = 22;

  /* Arc helper — fraction 0 = left end, 1 = right end */
  const arcPath = (sf, ef) => {
    const a1 = Math.PI - Math.PI * sf;
    const a2 = Math.PI - Math.PI * ef;
    const large = ef - sf > 0.5 ? 1 : 0;
    return [
      `M ${CX + RADIUS * Math.cos(a1)} ${CY - RADIUS * Math.sin(a1)}`,
      `A ${RADIUS} ${RADIUS} 0 ${large} 1`,
      `${CX + RADIUS * Math.cos(a2)} ${CY - RADIUS * Math.sin(a2)}`,
    ].join(" ");
  };

  const scoreToFraction = (score, ratingsRange) => {
    const num = Number(score);

    if (!ratingsRange || Object.keys(ratingsRange).length === 0) {
      return Math.min(Math.max(num / 100, 0), 1);
    }

    for (let i = 0; i < ORDERED_KEYS.length; i++) {
      const key = ORDERED_KEYS[i];
      const r = ratingsRange[key];
      if (!r) continue;

      const bMin = i === 0 ? 0 : (ratingsRange[ORDERED_KEYS[i - 1]]?.max ?? r.min);
      const bMax = r.max;

      if (num <= bMax || i === ORDERED_KEYS.length - 1) {
        const t = bMax === bMin ? 1 : Math.min(Math.max((num - bMin) / (bMax - bMin), 0), 1);
        return (i + t) * SEGMENT_FRACTION;
      }
    }
    return 1;
  };

  /* Needle */
  const fraction = scoreToFraction(score, ratingsRange);
  const needleAngle = Math.PI - Math.PI * fraction;
  const needleLen = RADIUS - STROKE / 2 - 6;
  const nx = CX + needleLen * Math.cos(needleAngle);
  const ny = CY - needleLen * Math.sin(needleAngle);

  /* Boundary score labels (pulled from ratingsRange) */
  const boundaryLabels = (() => {
    if (!ratingsRange || Object.keys(ratingsRange).length === 0) return [];
    const out = [{ f: 0, text: "0" }];
    ORDERED_KEYS.forEach((key, i) => {
      const r = ratingsRange[key];
      if (r) out.push({ f: (i + 1) * SEGMENT_FRACTION, text: String(Math.round(r.max)) });
    });
    return out;
  })();

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>

      {/* ── background track ── */}
      <path
        d={arcPath(0, 1)}
        fill="none"
        stroke="var(--color-border-primary)"
        strokeWidth={STROKE}
        strokeLinecap="round"
      />

      {/* ── equal-split colored segments ── */}
      {ORDERED_KEYS.map((key, i) => (
        <path
          key={key}
          d={arcPath(i * SEGMENT_FRACTION, (i + 1) * SEGMENT_FRACTION)}
          fill="none"
          stroke={bucketColors[key] ?? "#9ca3af"}
          strokeWidth={STROKE}
          strokeLinecap="butt"
        />
      ))}

      {/* ── white divider lines at each segment boundary ── */}
      {[1, 2, 3, 4].map((i) => {
        const angle = Math.PI - Math.PI * (i * SEGMENT_FRACTION);
        const r1 = RADIUS - STROKE / 2;
        const r2 = RADIUS + STROKE / 2;
        return (
          <line
            key={i}
            x1={CX + r1 * Math.cos(angle)} y1={CY - r1 * Math.sin(angle)}
            x2={CX + r2 * Math.cos(angle)} y2={CY - r2 * Math.sin(angle)}
            stroke="var(--color-surface)"
            strokeWidth={2.5}
          />
        );
      })}

      {/* ── boundary score labels outside the arc ── */}
      {/* {boundaryLabels.map(({ f, text }) => {
        const angle = Math.PI - Math.PI * f;
        const labelR = RADIUS + STROKE / 2 + 13;
        return (
          <text
            key={text}
            x={CX + labelR * Math.cos(angle)}
            y={CY - labelR * Math.sin(angle)}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="9"
            fontWeight="600"
            fill="var(--color-text-muted)"
          >
            {text}
          </text>
        );
      })} */}

      {/* ── needle ── */}
      <line
        x1={CX} y1={CY}
        x2={nx} y2={ny}
        stroke="var(--color-text-primary)"
        strokeWidth={3}
        strokeLinecap="round"
      />
      <circle cx={CX} cy={CY} r={8} fill="var(--color-text-primary)" />
      <circle cx={CX} cy={CY} r={4} fill="var(--color-surface)" />

      {/* ── centre score ── */}
      {/* <text
        x={CX} y={CY + 36}
        textAnchor="middle"
        fontSize="var(--font-size-2xl)"
        fontWeight="var(--font-weight-bold)"
        fill="var(--color-text-primary)"
      >
        {Number(score).toFixed(1)}
      </text> */}
    </svg>
  );
};

/* ── Build legend items from ratingsRange ── */
const buildGaugeSegments = (ratingsRange, bucketColors) => {
  const orderedKeys = ["Strong Sell", "Sell", "Hold", "Buy", "Strong Buy"];
  if (!ratingsRange || Object.keys(ratingsRange).length === 0) {
    return orderedKeys.map((k) => ({ label: k, range: "—", color: bucketColors[k] || "#9ca3af" }));
  }
  const items = orderedKeys
    .map((k) => ({ label: k, max: ratingsRange[k]?.max ?? null, color: bucketColors[k] || "#9ca3af" }))
    .filter((i) => i.max !== null);
  return items.map((item, idx) => {
    const displayMin = idx === 0 ? 0 : parseFloat((items[idx - 1].max + 0.1).toFixed(1));
    const displayMax = parseFloat(item.max.toFixed(1));
    return { label: item.label, range: `${fmtL(displayMin, 1)}–${fmtL(displayMax, 1)}`, color: item.color };
  });
};

/* ── Breakdown Row (expandable) ── */
const BreakdownRow = ({ label, color, count, tickers, navigate }) => {
  const [open, setOpen] = useState(false);
  const hasItems = tickers.length > 0;
  return (
    <div>
      <div
        onClick={() => hasItems && setOpen(!open)}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "var(--spacing-sm) var(--spacing-md)", cursor: hasItems ? "pointer" : "default", borderBottom: "1px solid var(--color-border-primary)", transition: "background 0.15s" }}
        onMouseEnter={(e) => { if (hasItems) e.currentTarget.style.backgroundColor = "var(--color-surface-hover)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "var(--spacing-sm)" }}>
          <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: color, flexShrink: 0 }} />
          <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-base)", fontWeight: "var(--font-weight-semibold)", color: "var(--color-text-primary)" }}>{label}:</Typography>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--spacing-sm)" }}>
          <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-base)", fontWeight: "var(--font-weight-bold)", color: "var(--color-text-primary)" }}>{count} stock{count !== 1 ? "s" : ""}</Typography>
          {hasItems && (
            <IconButton size="small" sx={{ color: "var(--color-text-muted)", p: 0.25 }}>
              {open ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          )}
        </div>
      </div>
      <Collapse in={open}>
        <div style={{ padding: "var(--spacing-sm) var(--spacing-md) var(--spacing-sm) var(--spacing-2xl)", backgroundColor: "var(--color-bg-inset)", display: "flex", flexWrap: "wrap", gap: "var(--spacing-sm)" }}>
          {tickers.map((t) => (
            <Chip key={t} label={t} size="small" onClick={() => navigate(`/stocks/${t}/details`)}
              sx={{ bgcolor: `${color}18`, color, fontWeight: "var(--font-weight-bold)", fontSize: "var(--font-size-sm)", cursor: "pointer", border: `1px solid ${color}30`, "&:hover": { bgcolor: `${color}30` } }}
            />
          ))}
        </div>
      </Collapse>
    </div>
  );
};

/* ── Holdings Score Bar Chart ── */
const HoldingsBarChart = ({ holdings }) => {
  const data = holdings
    .map((h) => ({ ticker: h.ticker, score: Number(h.overall_score || 0), grade: h.overall_grade || "—", color: gradeColor(h.overall_grade) }))
    .sort((a, b) => b.score - a.score);
  return (
    <div style={{ width: "100%", height: Math.max(data.length * 48 + 40, 160) }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 8, right: 40, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--color-border-primary)" />
          <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12, fill: "var(--color-text-muted)" }} axisLine={{ stroke: "var(--color-border-primary)" }} tickLine={false} />
          <YAxis type="category" dataKey="ticker" width={56} tick={{ fontSize: 13, fontWeight: 600, fill: "var(--color-primary)" }} axisLine={false} tickLine={false} />
          <RechartsTooltip
            content={({ active, payload }) => {
              if (active && payload?.length) {
                const d = payload[0].payload;
                return (
                  <div style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-md)", padding: "var(--spacing-sm) var(--spacing-md)" }}>
                    <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-bold)", color: "var(--color-text-primary)", mr: 1 }}>{d.ticker}</Typography>
                    <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-sm)", color: d.color, fontWeight: "var(--font-weight-semibold)" }}>FA Score: {d.score} — FA Grade: {d.grade}</Typography>
                  </div>
                );
              }
              return null;
            }}
          />
          <Bar dataKey="score" radius={[0, 6, 6, 0]} barSize={20}>
            {data.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ── Performer Card ── */
const PerformerCard = ({ holding, type, navigate }) => {
  const isTop = type === "top";
  return (
    <div
      onClick={() => navigate(`/stocks/${holding.ticker}/details`)}
      style={{ flex: 1, minWidth: 180, backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-lg)", padding: "var(--spacing-md) var(--spacing-lg)", cursor: "pointer", transition: "all var(--transition-fast)", borderLeft: `3px solid ${isTop ? "var(--color-success)" : "var(--color-error)"}` }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--color-surface-hover)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "var(--color-surface)"; }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "var(--spacing-sm)", marginBottom: "var(--spacing-sm)" }}>
        {isTop ? <TrendingUpIcon sx={{ fontSize: 18, color: "var(--color-success)" }} /> : <TrendingDownIcon sx={{ fontSize: 18, color: "var(--color-error)" }} />}
        <Typography sx={{ fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-semibold)", color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          {isTop ? "Today's Top" : "Today's Bottom"}
        </Typography>
      </div>
      <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-lg)", fontWeight: "var(--font-weight-bold)", color: "var(--color-primary)", display: "block" }}>{holding.ticker}</Typography>
      <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)", display: "block", mb: "var(--spacing-sm)" }}>{holding.company_name}</Typography>
      <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-base)", fontWeight: "var(--font-weight-bold)", color: Number(holding.daily_change_pct) >= 0 ? "var(--color-success)" : "var(--color-error)" }}>
        {fmtPercent(holding.daily_change_pct)} ({sym}{fmtNumber(holding.daily_change)})
      </Typography>
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   HEALTH CHECK TAB
══════════════════════════════════════════════════════ */
const HealthCheckTab = ({ portfolio }) => {
  const navigate = useNavigate();
  const bucketColors = useBucketColors();

  const [healthData, setHealthData] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [healthError, setHealthError] = useState(null);
  const [aiNarrative, setAiNarrative] = useState("");
  const [aiNarrativeLoading, setAiNarrativeLoading] = useState(false);

  useEffect(() => {
    const fetchHealth = async () => {
      setHealthLoading(true);
      setHealthError(null);

      const res = await getPortfolioHealthScore(portfolio.name);
      if (res?.authExpired) { navigate("/login"); return; }

      if (res?.success && res.data?.response_data) {
        const data = res.data.response_data;
        setHealthData(data);
        setHealthLoading(false); // ← MOVED HERE: page renders immediately with health data

        // Narrative loads independently — only its own section shows skeleton
        setAiNarrativeLoading(true);
        try {
          const sanitizedHoldings = (data.holdings || []).map((h) => {
            const gradeClean = h.overall_grade?.trim() || null;
            return {
              company_name: h.company_name || "",
              gain_loss_pct: String(h.gain_loss_pct || "0"),
              overall_grade: gradeClean,
              overall_score: gradeClean ? String(h.overall_score || "0") : null,
              shares: String(h.shares || "0"),
              ticker: h.ticker || "",
              weight_pct: String(h.weight_pct || "0"),
            };
          });
          const sanitizedBreakdown = {};
          Object.entries(data.breakdown || {}).forEach(([ticker, rating]) => {
            sanitizedBreakdown[ticker] = rating?.trim() || null;
          });
          const narRes = await getHealthNarrative({
            breakdown: sanitizedBreakdown,
            health_grade: data.health_grade?.trim() || null,
            health_score: String(data.health_score || "0"),
            holdings: sanitizedHoldings,
            portfolio_name: data.portfolio_name || portfolio.name,
            total_cost: String(data.total_cost || "0"),
            total_gain_loss: String(data.total_gain_loss || "0"),
            total_gain_loss_pct: String(data.total_gain_loss_pct || "0"),
            total_value: String(data.total_value || "0"),
          });
          if (narRes?.authExpired) { navigate("/login"); return; }
          if (narRes?.success && narRes.data?.ai_narrative) setAiNarrative(narRes.data.ai_narrative);
        } catch (e) { /* silent — narrative is non-critical */ }
        setAiNarrativeLoading(false);

      } else {
        setHealthError("Unable to load health score. Please try again.");
        setHealthLoading(false); // ← also needed here for error path
      }
    };

    if (portfolio?.name) fetchHealth();
  }, [portfolio?.name, portfolio?.id]);

  const breakdownBuckets = useMemo(() => {
    if (!healthData) return [];
    const breakdown = healthData.breakdown || {};
    const map = {};
    RATING_CATEGORIES.forEach((c) => { map[c.key] = []; });
    Object.entries(breakdown).forEach(([ticker, rating]) => {
      if (!rating) { if (map["Not Covered"]) map["Not Covered"].push(ticker); return; }
      const normalised = rating.toLowerCase();
      const key = Object.keys(map).find((k) => k.toLowerCase() === normalised);
      if (key) map[key].push(ticker);
      else if (map["Not Covered"]) map["Not Covered"].push(ticker);
    });
    return RATING_CATEGORIES.map((c) => ({
      ...c,
      color: bucketColors[c.key] ?? c.color,
      tickers: map[c.key] || [],
      count: (map[c.key] || []).length,
    }));
  }, [healthData, bucketColors]);

  const holdings = healthData?.holdings || [];
  const ratingsRange = healthData?.ratings_range || null;
  const healthScore = Number(healthData?.health_score || 0);
  // const gaugeSegments = useMemo(() => buildGaugeSegments(ratingsRange, bucketColors), [ratingsRange, bucketColors]);

  const sortedByDaily = useMemo(() =>
    [...holdings].filter((h) => h.daily_change_pct != null).sort((a, b) => Number(b.daily_change_pct) - Number(a.daily_change_pct)),
    [holdings]);

  const topPerformer = sortedByDaily[0] || null;
  const bottomPerformer = sortedByDaily.length > 1 ? sortedByDaily[sortedByDaily.length - 1] : null;

  if (healthLoading) return (
    <Box>
      <div style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-xl)", padding: "var(--spacing-xl)", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Skeleton variant="circular" width={180} height={180} sx={{ bgcolor: "var(--color-surface-hover)", mb: 3 }} />
        <Skeleton variant="rectangular" width={120} height={20} sx={{ bgcolor: "var(--color-surface-hover)", borderRadius: "var(--radius-sm)", mb: 2 }} />
        <Skeleton variant="rectangular" width={200} height={14} sx={{ bgcolor: "var(--color-surface-hover)", borderRadius: "var(--radius-sm)" }} />
      </div>
      <div style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-xl)", marginTop: "var(--spacing-lg)", padding: "var(--spacing-md)" }}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rectangular" width="100%" height={56} sx={{ bgcolor: "var(--color-surface-hover)", borderRadius: "var(--radius-md)", mb: 1.5 }} />
        ))}
      </div>
    </Box>
  );

  if (healthError) return (
    <div style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-xl)", padding: "var(--spacing-xl)", textAlign: "center" }}>
      <WarningAmberIcon sx={{ fontSize: 40, color: "var(--color-warning)", mb: 1.5 }} />
      <Typography variant="paragraph" sx={{ color: "var(--color-text-secondary)", mb: "var(--spacing-md)" }}>{healthError}</Typography>
      <Button size="small" onClick={() => window.location.reload()} sx={{ color: "var(--color-primary)", textTransform: "none", fontSize: "var(--font-size-sm)" }}>Retry</Button>
    </div>
  );

  return (
    <Box>
      {/* Gauge + Breakdown */}
      <div style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-xl)", borderTop: "3px solid var(--color-primary)", overflow: "hidden", marginBottom: "var(--spacing-lg)" }}>
        <div style={{ padding: "var(--spacing-md) var(--spacing-xl)", borderBottom: "1px solid var(--color-border-primary)" }}>
          <Typography variant="subtitle" sx={{ color: "var(--color-text-primary)" }}>How Healthy Is Your Portfolio</Typography>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          {/* Left: Gauge */}
          <div style={{ flex: "1 1 320px", padding: "var(--spacing-lg) var(--spacing-xl)", borderRight: "1px solid var(--color-border-primary)", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-base)", fontWeight: "var(--font-weight-bold)", color: "var(--color-text-primary)", mb: "var(--spacing-md)" }}>FA Score</Typography>
            <SemiGauge score={healthScore} ratingsRange={ratingsRange} />
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "var(--spacing-sm)", marginTop: "var(--spacing-xs)" }}>
              <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "var(--spacing-sm) var(--spacing-md)", marginBottom: "var(--spacing-sm)" }}>
                {/* {gaugeSegments.map((item) => (
                  <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: item.color, flexShrink: 0 }} />
                    <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)", lineHeight: 1 }}>{item.label}</Typography>
                  </div>
                ))} */}
              </div>
              <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>Your rating FA Score is</Typography>
              <div style={{ display: "flex", alignItems: "baseline", gap: "var(--spacing-sm)" }}>
                <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-2xl)", fontWeight: "var(--font-weight-bold)", color: "var(--color-text-primary)" }}>{fmtL(healthScore, 1)}</Typography>
                <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-base)", color: "var(--color-text-muted)" }}>/ 100</Typography>
              </div>
            </div>
          </div>
          {/* Right: Breakdown */}
          <div style={{ flex: "1 1 320px", padding: "var(--spacing-lg) 0 0 0", display: "flex", flexDirection: "column" }}>
            <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-base)", fontWeight: "var(--font-weight-bold)", color: "var(--color-text-primary)", px: "var(--spacing-xl)", mb: "var(--spacing-md)" }}>Breakdown</Typography>
            <div>
              {breakdownBuckets.map((b) => (
                <BreakdownRow key={b.key} label={b.key} color={b.color} count={b.count} tickers={b.tickers} navigate={navigate} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* AI Health Narrative — typewriter enabled */}
      <AIHealthNarrative aiNarrativeLoading={aiNarrativeLoading} aiNarrative={aiNarrative} />

      {/* Top / Bottom Performers */}
      {holdings.length >= 2 && topPerformer && bottomPerformer && (
        <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2} mb="var(--spacing-lg)">
          <PerformerCard holding={topPerformer} type="top" navigate={navigate} />
          <PerformerCard holding={bottomPerformer} type="bottom" navigate={navigate} />
        </Box>
      )}

      {/* Holdings Score Bar Chart */}
      {holdings.length > 0 && (
        <div style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-lg)", overflow: "hidden", marginBottom: "var(--spacing-lg)" }}>
          <div style={{ padding: "var(--spacing-sm) var(--spacing-md)", borderBottom: "1px solid var(--color-border-primary)" }}>
            <Typography sx={{ fontSize: "var(--font-size-base)", fontWeight: "var(--font-weight-bold)", color: "var(--color-text-primary)" }}>Holdings Overall FA Score</Typography>
          </div>
          <div style={{ padding: "var(--spacing-md)" }}>
            <HoldingsBarChart holdings={holdings} />
          </div>
        </div>
      )}

      {/* Value / Gain Cards */}
      <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
        <div style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-lg)", padding: "var(--spacing-lg)", display: "flex", flexDirection: "column" }}>
          <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-semibold)", color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", mb: "var(--spacing-sm)" }}>Portfolio Value</Typography>
          <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-2xl)", fontWeight: "var(--font-weight-bold)", color: "var(--color-success)" }}>{sym}{fmtNumber(healthData?.total_value)}</Typography>
          <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-base)", color: "var(--color-text-muted)", mt: "2px" }}>Cost: {sym}{fmtNumber(healthData?.total_cost)}</Typography>
        </div>
        <div style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-lg)", padding: "var(--spacing-lg)", display: "flex", flexDirection: "column" }}>
          <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-semibold)", color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", mb: "var(--spacing-sm)" }}>Total Gain/Loss</Typography>
          <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-2xl)", fontWeight: "var(--font-weight-bold)", color: Number(healthData?.total_gain_loss) >= 0 ? "var(--color-success)" : "var(--color-error)" }}>{Number(healthData?.total_gain_loss) >= 0 ? "+" : ""}{sym}{fmtNumber(healthData?.total_gain_loss)}</Typography>
          <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-base)", color: Number(healthData?.total_gain_loss_pct) >= 0 ? "var(--color-success)" : "var(--color-error)", mt: "2px" }}>{fmtPercent(healthData?.total_gain_loss_pct)}</Typography>
        </div>
      </Box>
    </Box>
  );
};

export default HealthCheckTab;